import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { EcmMaterialModule, InsecablePipe, PhoneNumberPipe } from '@ecm/material';
import { PortfolioService } from '@ecm/portfolio';
import { EcmRouterModule, Link, LinkService, TitleService, TrackingData } from '@ecm/router';
import { KenobiParams, Status } from '@ecm/tracker';

import { Dashboard } from '../../../../../business/api/dashboard/dashboard';
import { CrossroadsViewModelMapper } from '../../../../../business/mapper/crossroads/crossroads.view-model-mapper';
import { ActiveContractViewModel } from '../../../../../business/model/crossroads/contracts-options/active-contract.view-model';
import { CrossroadsViewModel } from '../../../../../business/model/crossroads/crossroads.view-model';
import { KenobiCodes } from '../../../../../business/model/tracking/kenobiCodes';
import { TrackedPage } from '../../../../../business/model/tracking/trackedPage.enum';
import { TrackingService } from '../../../../../services/tracking.service';
import { ParamsUtils } from '../../../../../utils/params.utils';
import { AbstractParentComponent } from '../../../../shared/abstract-parent.component';
import { CrossroadsUtils } from '../../../utils/crossroads.utils';
import { RemoveContractUtils } from '../removeContract.utils';

@Component({
    selector: 'contract-selector',
    templateUrl: 'contract-selector.html',
    styleUrls: ['contract-selector.css'],
    standalone: true,
    imports: [EcmMaterialModule, AsyncPipe, EcmRouterModule, TranslateModule, PhoneNumberPipe, InsecablePipe],
})
export class ContractSelectorComponent extends AbstractParentComponent implements OnInit {
    /**
     * Transaction properties for BCO monitoring
     */
    private readonly pageId: string;

    public contractViewModel: CrossroadsViewModel;
    public activeContractViewModel: ActiveContractViewModel[];

    public removeContractLink: Link;

    private readonly trackingService = inject(TrackingService);
    private readonly portfolioService = inject(PortfolioService);
    private readonly titleService = inject(TitleService);
    private readonly translate = inject(TranslateService);
    private readonly linkService = inject(LinkService);

    public constructor() {
        super();
        this.translate.use('fr');
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.translate.get('dashboard').subscribe((dashboardTranslations: any) => {
            this.titleService.setTitle(dashboardTranslations['removeContracts']['title']);
            EcmDispatcherModule.sendBackRoute('accueil');
            this.portfolioService.getRawContracts().subscribe((contracts) => {
                const isSosh = this.isSoshBrand()();
                this.trackView();
                CrossroadsUtils.orderModel(
                    (this.contractViewModel = CrossroadsViewModelMapper.getCrossroadsViewModel(
                        new Dashboard(),
                        contracts,
                        dashboardTranslations['contractsAndOptions'],
                        null,
                        null,
                        null,
                        true,
                    )),
                    isSosh,
                );
                if (this.contractViewModel.contracts) {
                    this.activeContractViewModel = RemoveContractUtils.extractActiveContract(
                        this.contractViewModel.contracts,
                    );
                }
            });
            this.removeContractLink = { inApp: 'retirer-contrat/{{cid}}' };
        });
    }

    /**
     * Get linkParam
     * @param {ActiveContractViewModel} contract
     * @return {Map<string, string>}
     */
    public getLinkParam(contract: ActiveContractViewModel): Map<string, string> {
        return ParamsUtils.cidMap(contract.cid);
    }

    /**
     * Open remove contract confirmation page
     * @param {ActiveContractViewModel} contract
     */
    public removeContract(contract: ActiveContractViewModel): void {
        this.linkService.openLink(
            { inApp: 'retirer-contrat/{{cid}}' },
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.contractSelector,
                    trackingArea: 'care_retirer_un_contrat',
                    trackingName: 'retirer_un_contrat',
                },
            }),
            ParamsUtils.cidMap(contract.cid),
        );
    }

    /**
     * Tracks view in kenobi and TMS
     */
    private trackView(): void {
        this.trackingService.trackContractSelectorView();
        this.trackingService.trackKenobi(
            new KenobiParams(KenobiCodes.LISUPCTR['functionality'], KenobiCodes.LISUPCTR['rubric'], Status.OK),
            this.pageId,
        );
    }
}
